import { Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { ComponentType, ReactNode } from "react";
import type { IconProps } from "../../../../shared/icons";
import {
  RegistrationProcessorIcon1,
  RegistrationControllerIcon1,
  DpiaIcon1,
  SystemIcon1,
  PermissionIcon1,
  MailIcon,
} from "../../../../shared/icons";
import type { GridViewUser } from "../../../../api/users";
import { Drawer } from "../../../../shared/Drawer";
import { DynamicForm } from "../../../../dynamicForm";
import { usePrivacyNavigate } from "../../../../shared/usePrivacyNavigate";
import { RESPONSIBLE_ENTITIES_PATHS } from "../constants";

export const MainDrawerContent = ({ user }: { user: GridViewUser }) => {
  const { t } = useTranslation();
  const { navigate } = usePrivacyNavigate();

  return (
    <Drawer.Content>
      <DynamicForm.InlineFormSection title={t("User.Grid.Drawer.PersonalInformationTitle")}>
        <DynamicForm.InlineFieldPrimary
          label={t("User.Grid.Drawer.PersonalInformation.Name.Label")}
          htmlFor="name"
          input={
            <InlineFieldValue>
              {user.firstName} {user.lastName}
            </InlineFieldValue>
          }
        />
        <DynamicForm.InlineFieldPrimary
          label={t("User.Grid.EmailHeader")}
          htmlFor="email"
          input={<InlineFieldValue Icon={MailIcon}>{user.email}</InlineFieldValue>}
        />
      </DynamicForm.InlineFormSection>

      <Drawer.GoToSubDrawerButtonsWrapper>
        <Drawer.GoToSubDrawerButton
          Icon={RegistrationControllerIcon1}
          label={t("User.Grid.RegistrationsWhereResponsibleHeader")}
          count={user.registrationsWhereResponsible.length}
          onClick={() => navigate(RESPONSIBLE_ENTITIES_PATHS.registrationsWhereResponsible)}
        />
        <Drawer.GoToSubDrawerButton
          Icon={RegistrationProcessorIcon1}
          label={t("User.Grid.ProcessorRegistrationsWhereResponsibleHeader")}
          count={user.processorRegistrationsWhereResponsible.length}
          onClick={() => navigate(RESPONSIBLE_ENTITIES_PATHS.processorRegistrationsWhereResponsible)}
        />
        <Drawer.GoToSubDrawerButton
          Icon={DpiaIcon1}
          label={t("User.Grid.DPIAsWhereResponsibleHeader")}
          count={user.dpiasWhereResponsible.length}
          onClick={() => navigate(RESPONSIBLE_ENTITIES_PATHS.dpiasWhereResponsible)}
        />
        <Drawer.GoToSubDrawerButton
          Icon={SystemIcon1}
          label={t("User.Grid.SystemsWhereResponsibleHeader")}
          count={user.systemsWhereResponsible.length}
          onClick={() => navigate(RESPONSIBLE_ENTITIES_PATHS.systemsWhereResponsible)}
        />
        <Drawer.GoToSubDrawerButton
          Icon={PermissionIcon1}
          label={t("User.Grid.Drawer.Permissions.Label")}
          onClick={() => navigate("roles")}
        />
      </Drawer.GoToSubDrawerButtonsWrapper>
    </Drawer.Content>
  );
};

const InlineFieldValue = ({ children, Icon }: { children: ReactNode; Icon?: ComponentType<IconProps> }) => {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {Icon && <Icon size={16} />}
      <Typography
        sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: theme => theme.typography.pxToRem(14) }}
      >
        {children}
      </Typography>
    </Stack>
  );
};
